import {
  Message,
  Modal,
  Notification,
} from '@arco-design/web-react';
import type { MessageProps } from '@arco-design/web-react/es/Message/interface';
import type { ConfirmProps } from '@arco-design/web-react/es/Modal/confirm';
import type { NotificationProps } from '@arco-design/web-react/es/Notification/interface';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import type { PortalOperationFeedback } from '../runtime/wujieLoader';
import type { FormLeavePrompt } from '../runtime/formSessionManager';
import { PortalFormLeaveDialog, type PortalFormLeaveRequest } from './PortalFormLeaveDialog';

type MessageInput = MessageProps | string;

export type PortalOverlay = Readonly<{
  message: Readonly<{
    info: (input: MessageInput) => void;
    success: (input: MessageInput) => void;
    warning: (input: MessageInput) => void;
    error: (input: MessageInput) => void;
  }>;
  confirm: (config: ConfirmProps) => void;
  notification: Readonly<{
    info: (config: NotificationProps) => void;
    success: (config: NotificationProps) => void;
    warning: (config: NotificationProps) => void;
    error: (config: NotificationProps) => void;
  }>;
  reportOperationFeedback: (feedback: PortalOperationFeedback) => void;
  promptFormLeave: FormLeavePrompt;
}>;

const PortalOverlayContext = createContext<PortalOverlay | undefined>(undefined);

function invokeOverlay(kind: string, action: () => unknown) {
  try {
    const result = action();
    if (result && typeof Reflect.get(Object(result), 'then') === 'function') {
      void Promise.resolve(result).catch(error => {
        console.error(`Portal ${kind} overlay failed`, error);
      });
    }
  } catch (error) {
    console.error(`Portal ${kind} overlay failed`, error);
  }
}

export function PortalOverlayProvider({ children }: PropsWithChildren) {
  const [formLeaveRequest, setFormLeaveRequest] = useState<PortalFormLeaveRequest>();
  const formLeaveRef = useRef<PortalFormLeaveRequest | undefined>(undefined);
  useEffect(() => () => formLeaveRef.current?.finish('CANCEL'), []);
  const [messageApi, messageHolder] = Message.useMessage();
  const [modalApi, modalHolder] = Modal.useModal();
  const [notificationApi, notificationHolder] = Notification.useNotification();
  const messageApiRef = useRef(messageApi);
  const modalApiRef = useRef(modalApi);
  const notificationApiRef = useRef(notificationApi);
  messageApiRef.current = messageApi;
  modalApiRef.current = modalApi;
  notificationApiRef.current = notificationApi;

  const value = useMemo<PortalOverlay>(() => {
    const message = {
      info: (input: MessageInput) => invokeOverlay(
        'message', () => messageApiRef.current.info?.(input)),
      success: (input: MessageInput) => invokeOverlay(
        'message', () => messageApiRef.current.success?.(input)),
      warning: (input: MessageInput) => invokeOverlay(
        'message', () => messageApiRef.current.warning?.(input)),
      error: (input: MessageInput) => invokeOverlay(
        'message', () => messageApiRef.current.error?.(input)),
    };
    return {
      message,
      promptFormLeave: input => new Promise(resolve => {
        formLeaveRef.current?.finish('CANCEL');
        if (input.signal.aborted) { resolve('CANCEL'); return; }
        let settled = false;
        const finish = (choice: 'SAVE' | 'DISCARD' | 'CANCEL') => {
          if (settled) return;
          settled = true;
          input.signal.removeEventListener('abort', abort);
          if (formLeaveRef.current === request) {
            formLeaveRef.current = undefined; setFormLeaveRequest(undefined);
          }
          resolve(choice);
        };
        const abort = () => finish('CANCEL');
        const request = Object.freeze({ input, finish });
        formLeaveRef.current = request; setFormLeaveRequest(request);
        input.signal.addEventListener('abort', abort, { once: true });
      }),
      confirm: config => invokeOverlay(
        'modal', () => modalApiRef.current.confirm?.(config)),
      notification: {
        info: config => invokeOverlay(
          'notification', () => notificationApiRef.current.info?.(config)),
        success: config => invokeOverlay(
          'notification', () => notificationApiRef.current.success?.(config)),
        warning: config => invokeOverlay(
          'notification', () => notificationApiRef.current.warning?.(config)),
        error: config => invokeOverlay(
          'notification', () => notificationApiRef.current.error?.(config)),
      },
      reportOperationFeedback: feedback => {
        message[feedback.type](feedback);
      },
    };
  }, []);

  return (
    <PortalOverlayContext.Provider value={value}>
      {children}
      {messageHolder}
      {modalHolder}
      {notificationHolder}
      <PortalFormLeaveDialog request={formLeaveRequest} />
    </PortalOverlayContext.Provider>
  );
}

export function usePortalOverlay() {
  const overlay = useContext(PortalOverlayContext);
  if (!overlay) {
    throw new Error('usePortalOverlay 必须在 PortalOverlayProvider 内使用');
  }
  return overlay;
}
